// /admin/people/users/new and /admin/people/users/:alias/edit — minimal
// admin form for IdentityItem rows. Schema-equivalent to SCIM POST /Users:
// {alias, ringUserId?, amazonCustomerId?}. The audit row's `source` field
// auto-tags this write as "admin"; SCIM ingest tags as "scim".

import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ApiError, useApiErrorHandler } from "@/api";
import { useToast } from "@/components";
import { userClient } from "@/data/clients/userClient";
import "../AdminShared.css";

export function UserFormPage(): JSX.Element {
  const { alias: editAlias } = useParams<{ alias: string }>();
  const isEdit = Boolean(editAlias);
  const navigate = useNavigate();
  const handleApiError = useApiErrorHandler();
  const toast = useToast();

  const [alias, setAlias] = useState("");
  const [ringUserId, setRingUserId] = useState("");
  const [amazonCustomerId, setAmazonCustomerId] = useState("");
  const [loading, setLoading] = useState(isEdit);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isEdit || !editAlias) return;
    let cancelled = false;
    void (async () => {
      try {
        const u = await userClient.get(editAlias);
        if (cancelled) return;
        setAlias(u.alias);
        setRingUserId(u.ring_user_id ?? "");
        setAmazonCustomerId(u.amazon_customer_id ?? "");
      } catch (err) {
        if (err instanceof ApiError) {
          handleApiError(err, { actionLabel: "Load user" });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, editAlias]);

  const canSave = alias.trim().length > 0 && !submitting;

  const handleSave = useCallback(async () => {
    if (!canSave) return;
    setSubmitting(true);
    try {
      if (isEdit && editAlias) {
        await userClient.update(editAlias, {
          ringUserId: ringUserId.trim() || undefined,
          amazonCustomerId: amazonCustomerId.trim() || undefined,
        });
        toast.success({ title: `User '${editAlias}' updated` });
      } else {
        await userClient.create({
          alias: alias.trim(),
          ringUserId: ringUserId.trim() || undefined,
          amazonCustomerId: amazonCustomerId.trim() || undefined,
        });
        toast.success({ title: `User '${alias.trim()}' created` });
      }
      navigate("/admin/people");
    } catch (err) {
      if (err instanceof ApiError) {
        handleApiError(err, { actionLabel: "Save user" });
      }
    } finally {
      setSubmitting(false);
    }
  }, [canSave, isEdit, editAlias, alias, ringUserId, amazonCustomerId, toast, handleApiError, navigate]);

  if (loading) {
    return (
      <section className="admin-page">
        <header className="v6-page-header">
          <div className="breadcrumbs">
            <Link to="/admin/people">← Groups &amp; users</Link>
          </div>
          <h1>Loading…</h1>
        </header>
      </section>
    );
  }

  return (
    <section className="admin-page">
      <header className="v6-page-header">
        <div className="breadcrumbs">
          <Link to="/admin/people">← Groups &amp; users</Link>
        </div>
        <h1>{isEdit ? `Edit user — ${editAlias ?? ""}` : "New user"}</h1>
        <div className="caption">
          Writes the same identity row as your IdP&apos;s SCIM provisioner.
          The audit log tags this entry as a UI change rather than a sync.
        </div>
      </header>

      <div
        style={{
          maxWidth: 560,
          marginTop: 16,
          padding: 20,
          background: "var(--ring-surface-bg, transparent)",
          borderRadius: 8,
          border: "1px solid var(--ring-border, #2a2d3a)",
        }}
      >
        <FormRow label="Alias" htmlFor="user-alias" hint="The user's IdP alias (immutable).">
          <input
            id="user-alias"
            type="text"
            value={alias}
            onChange={(e) => setAlias(e.target.value)}
            disabled={isEdit}
            placeholder="e.g. jdoe"
          />
        </FormRow>
        <FormRow
          label="Ring user ID"
          htmlFor="user-ring-id"
          hint="REB-internal handle. Leave blank to auto-mint on create."
        >
          <input
            id="user-ring-id"
            type="text"
            value={ringUserId}
            onChange={(e) => setRingUserId(e.target.value)}
            placeholder="(auto)"
          />
        </FormRow>
        <FormRow
          label="Amazon customer ID"
          htmlFor="user-amzn-id"
          hint="Legacy field; defaults to alias when blank."
        >
          <input
            id="user-amzn-id"
            type="text"
            value={amazonCustomerId}
            onChange={(e) => setAmazonCustomerId(e.target.value)}
            placeholder="(auto)"
          />
        </FormRow>

        <div style={{ display: "flex", gap: 8, marginTop: 24, justifyContent: "flex-end" }}>
          <button
            type="button"
            className="v6-btn ghost"
            onClick={() => navigate("/admin/people")}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="v6-btn primary"
            onClick={() => void handleSave()}
            disabled={!canSave}
          >
            {isEdit ? "Save changes" : "Create user"}
          </button>
        </div>
      </div>
    </section>
  );
}

function FormRow({
  label,
  htmlFor,
  hint,
  children,
}: {
  label: string;
  htmlFor: string;
  hint?: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div style={{ marginBottom: 16 }}>
      <label
        htmlFor={htmlFor}
        style={{ display: "block", fontWeight: 500, fontSize: 13, marginBottom: 6 }}
      >
        {label}
      </label>
      {children}
      {hint ? (
        <div
          style={{
            marginTop: 4,
            fontSize: 12,
            color: "var(--ring-neutral-300)",
          }}
        >
          {hint}
        </div>
      ) : null}
    </div>
  );
}
